import React, { useState } from 'react';
import { IoTrash } from 'react-icons/io5';
import TextEdit from '../../../molecules/TextEdit';
import NewTooltip from '../../../atoms/NewTooltip';
import { formatCurrency, formatDate, formatBytes } from '../structuringUtils';
import { Document } from '../OperationPageViewBackend';
import { toast } from 'react-toastify';

interface OperationDetailViewProps {
    documents: Document[];
    totalReceivables: number;
    isDeleting: boolean;
    isOperationClosed: boolean;
    onDeleteDocument: (documentId: string) => void;
    onDeleteInstallment: (documentId: string, installmentNumber: string) => void;
    onReceivableChange?: (document: Document, receivable: import('../OperationPageViewBackend').Receivable) => void;
}

const OperationDetailView: React.FC<OperationDetailViewProps> = ({
    documents,
    totalReceivables,
    isDeleting,
    isOperationClosed,
    onDeleteDocument,
    onDeleteInstallment,
    onReceivableChange,
}) => {
    const [editValues, setEditValues] = useState<Record<string, { due_date: string; amount: string }>>({});
    const [focusedAmount, setFocusedAmount] = useState<string | null>(null);

    const handleValueChange = (docIndex: number, recIndex: number, field: 'due_date' | 'amount', value: string) => {
        const key = `${docIndex}-${recIndex}-${field}`;
        setEditValues(prev => ({
            ...prev,
            [key]: { ...prev[key], [field]: value }
        }));
    };

    const handleAmountBlur = (docIndex: number, recIndex: number) => {
        const sortedDocuments = [...documents].sort((a, b) => (a.document_title || '').localeCompare(b.document_title || '', 'pt-BR', { numeric: true }));
        const document = sortedDocuments[docIndex];
        const receivable = document?.receivables[recIndex];
        const raw = String(getCellValue(docIndex, recIndex, 'amount', receivable?.amount ?? 0)).replace(',', '.');
        const numeric = parseFloat(raw);
        const key = `${docIndex}-${recIndex}-amount`;

        if (isNaN(numeric) || numeric < 0 || !/^\d+(\.\d{1,2})?$/.test(raw.trim())) {
            toast.error('Valor inválido. Informe um valor monetário válido (ex: 1234,56)');
            return;
        }

        setFocusedAmount(null);
        setEditValues(prev => ({ ...prev, [key]: { ...prev[key], amount: String(numeric) } }));

        if (onReceivableChange && document && receivable) {
            onReceivableChange(document, { ...receivable, amount: numeric });
        }
    };

    const handleDueDateChange = (docIndex: number, recIndex: number, value: string) => {
        handleValueChange(docIndex, recIndex, 'due_date', value);
        const sortedDocuments = [...documents].sort((a, b) => (a.document_title || '').localeCompare(b.document_title || '', 'pt-BR', { numeric: true }));
        const document = sortedDocuments[docIndex];
        const receivable = document?.receivables[recIndex];
        if (onReceivableChange && document && receivable) {
            onReceivableChange(document, { ...receivable, due_date: value });
        }
    };

    const getCellValue = (docIndex: number, recIndex: number, field: 'due_date' | 'amount', originalValue: any) => {
        const key = `${docIndex}-${recIndex}-${field}`;
        return editValues[key]?.[field] ?? originalValue;
    };

    return (
        <div className="space-y-4">
            {[...documents].sort((a, b) => (a.document_title || '').localeCompare(b.document_title || '', 'pt-BR', { numeric: true })).map((document, index) => (
                <div key={index} className="bg-white dark:bg-slate-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">Documento {index + 1}</h4>
                        <NewTooltip content="Deletar documento" alignment="left">
                            <button
                                onClick={() => onDeleteDocument(document.document_id)}
                                disabled={isDeleting || isOperationClosed}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <IoTrash className="w-5 h-5" />
                            </button>
                        </NewTooltip>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-6">
                            <TextEdit label="ID do Documento:" value={document.document_id} disabled />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="Título:" value={document.document_title} disabled />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="CFOP:" value={document.cfop} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Data de Emissão:" value={formatDate(document.issue_date)} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Tamanho do XML:" value={formatBytes(document.xml_size_bytes)} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Recebíveis:" value={totalReceivables.toString()} disabled />
                        </div>
                    </div>

                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Dados do Cedente</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Nome:" value={document.assignor_name} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="CPF/CNPJ:" value={document.assignor_cnpj} disabled />
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Dados do Sacado</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Nome:" value={document.debtor_name} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="CPF/CNPJ:" value={document.debtor_cpf_cnpj} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="Telefone:" value={document.address_phone} disabled />
                            </div>
                            <div className="col-span-12">
                                <TextEdit label="Email:" value={document.debtor_email} disabled />
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Endereço Sacado</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Logradouro:" value={document.address_street} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="Número:" value={document.address_number} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Complemento:" value={document.address_complement} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Bairro:" value={document.address_district} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Cidade:" value={document.address_city} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="UF:" value={document.address_state} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="CEP:" value={document.address_zip_code} disabled />
                            </div>
                        </div>
                    </div>

                    {document.receivables.length > 0 && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Recebíveis</h4>
                            <div className="space-y-3">
                                {document.receivables.map((receivable, receivableIndex) => {
                                    const dueDate = receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '';

                                    return (
                                        <div key={receivableIndex} className="bg-gray-50 dark:bg-slate-700 p-3 rounded flex justify-between items-center">
                                            <div className="grid grid-cols-12 gap-4 flex-1">
                                                <div className="col-span-4">
                                                    <TextEdit label="Número:" value={receivable.receivable_number} disabled />
                                                </div>
                                                <div className="col-span-4">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vencimento:</label>
                                                    <input
                                                        type="text"
                                                        value={getCellValue(index, receivableIndex, 'due_date', dueDate)}
                                                        onChange={(e) => handleDueDateChange(index, receivableIndex, e.target.value)}
                                                        disabled={isOperationClosed}
                                                        placeholder="DD/MM/AAAA"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded bg-white dark:bg-slate-600 dark:text-gray-300 dark:border-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                                <div className="col-span-4">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor:</label>
                                                    <input
                                                        type="text"
                                                        value={focusedAmount === `${index}-${receivableIndex}` ? String(getCellValue(index, receivableIndex, 'amount', receivable.amount)) : formatCurrency(Number(String(getCellValue(index, receivableIndex, 'amount', receivable.amount)).replace(',', '.')))}
                                                        onFocus={() => setFocusedAmount(`${index}-${receivableIndex}`)}
                                                        onBlur={() => handleAmountBlur(index, receivableIndex)}
                                                        onChange={(e) => handleValueChange(index, receivableIndex, 'amount', e.target.value.replace(/[^\d,.]/g, ''))}
                                                        disabled={isOperationClosed}
                                                        placeholder="0,00"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded bg-white dark:bg-slate-600 dark:text-gray-300 dark:border-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <NewTooltip content="Deletar parcela" alignment="left">
                                                    <button
                                                        onClick={() => onDeleteInstallment(document.document_id, receivable.receivable_number)}
                                                        disabled={isDeleting || isOperationClosed}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <IoTrash className="w-4 h-4" />
                                                    </button>
                                                </NewTooltip>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default OperationDetailView;
